import React, { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import Button from '../../components/Button';
import { useToast } from '../../store/ToastContext';
import { formatDate, formatNumber } from '../../utils/helpers';
import { describeChange, manualRegistrationsApi } from './manualRegistrationsApi';
import {
  GST_RATE, MAX_BACKDATE_DAYS, PAYMENT_METHODS, PROOF_ACCEPT, addDays, changeTypeLabel, daysBetween, formatFileSize,
  formatINR, fromInputDate, parseAmount, priceBreakdown, toInputDate, todayISO, validateProofFile,
} from './manualRegistrationUtils';
import { CompanyPaymentHistory, SubscriptionStateBadge } from './ManualRegistrationParts';
import { SectionHeading } from './AdminWidgets';
import { BuildingIcon, FileCheckIcon, FileTextIcon, ReceiptIcon, SearchIcon, UploadIcon } from './ProductAdminIcons';
import ui from './ProductAdminDashboard.module.css';
import m from './ManualRegistrations.module.css';

const EMPTY_COMPANY = { name: '', admin_name: '', email: '', phone: '' };
const EMAIL = /^[^\s@]+@[^\s@]+\.[^\s@]{2,}$/;

function FieldError({ message }) {
  return message ? <span className={m.errorText} role="alert">{message}</span> : null;
}

const newKey = () => (window.crypto?.randomUUID ? window.crypto.randomUUID() : `k-${Date.now()}-${Math.random()}`);

// Register a new company, or renew / upgrade an existing one, with a manual payment
export default function ProductAdminManualRegistrationNew() {
  const navigate = useNavigate();
  const toast = useToast();
  const fileInput = useRef(null);
  const idempotencyKey = useRef(newKey());   // one key per form, so a repeated submit cannot activate twice

  const [plans, setPlans] = useState([]);
  const [mode, setMode] = useState('existing');
  const [search, setSearch] = useState('');
  const [results, setResults] = useState(null);
  const [searching, setSearching] = useState(false);
  const [company, setCompany] = useState(null);
  const [ownerId, setOwnerId] = useState('');
  const [newCompany, setNewCompany] = useState(EMPTY_COMPANY);
  const [history, setHistory] = useState(null);
  const [historyError, setHistoryError] = useState('');

  const [planId, setPlanId] = useState('');
  const [licences, setLicences] = useState('');
  const [startDate, setStartDate] = useState(todayISO());
  const [expiryEdit, setExpiryEdit] = useState(null);   // null = the suggested expiry
  const [amountEdit, setAmountEdit] = useState(null);
  const [overrideReason, setOverrideReason] = useState('');

  const [method, setMethod] = useState('upi');
  const [reference, setReference] = useState('');
  const [paymentDate, setPaymentDate] = useState(todayISO());
  const [remarks, setRemarks] = useState('');
  const [proof, setProof] = useState(null);
  const [proofPreview, setProofPreview] = useState(null);
  const [dragging, setDragging] = useState(false);
  const [sendEmail, setSendEmail] = useState(true);
  const [confirmed, setConfirmed] = useState(false);

  const [errors, setErrors] = useState({});
  const [submitError, setSubmitError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  useEffect(() => {
    manualRegistrationsApi.plans().then(setPlans).catch(() => setSubmitError('Unable to load the plans'));
  }, []);

  // Company search (existing companies), after a short pause in typing
  useEffect(() => {
    if (mode !== 'existing' || company) return undefined;
    if (search.trim().length < 2) { setResults(null); return undefined; }
    setSearching(true);
    const timer = setTimeout(async () => {
      try { setResults(await manualRegistrationsApi.searchCompanies(search)); } catch (e) { setResults([]); } finally { setSearching(false); }
    }, 300);
    return () => clearTimeout(timer);
  }, [search, mode, company]);

  // The selected company's payment and subscription history (online and manual payments)
  useEffect(() => {
    setHistory(null); setHistoryError('');
    if (!company) return undefined;
    let cancelled = false;
    manualRegistrationsApi.companyHistory(company.id)
      .then((items) => { if (!cancelled) setHistory(items); })
      .catch((err) => { if (!cancelled) setHistoryError(err.response?.data?.error || 'Unable to load the history'); });
    return () => { cancelled = true; };
  }, [company]);

  // Preview for image receipts; released when replaced or on leaving
  useEffect(() => {
    if (!proof || !proof.type.startsWith('image/')) { setProofPreview(null); return undefined; }
    const url = URL.createObjectURL(proof);
    setProofPreview(url);
    return () => URL.revokeObjectURL(url);
  }, [proof]);

  const plan = plans.find((p) => p.id === Number(planId)) || null;
  const licenceCount = Number(licences);
  // A plan only sells the licence counts of its own range
  const licenceRangeError = plan && licences !== ''
    && (!Number.isInteger(licenceCount) || licenceCount < plan.min_licences || licenceCount > plan.max_licences)
    ? `${plan.name} allows ${plan.min_licences} to ${plan.max_licences} licences` : '';
  const licencesValid = Boolean(plan) && licences !== '' && !licenceRangeError;
  const price = plan && licencesValid ? priceBreakdown(plan.price_per_licence, licenceCount) : null;
  const listPrice = price ? price.total : 0;
  const licenceChoices = plan && plan.max_licences - plan.min_licences < 60
    ? Array.from({ length: plan.max_licences - plan.min_licences + 1 }, (_, i) => plan.min_licences + i) : null;
  const amountText = amountEdit ?? (listPrice ? String(listPrice) : '');
  const amount = parseAmount(amountText);
  const overridden = Boolean(plan) && Number.isFinite(amount) && amount !== listPrice;

  // Start and expiry are editable; the expiry defaults to the plan's duration after the start
  // (plus the days left on an active paid plan when renewing)
  const change = useMemo(() => {
    const start = fromInputDate(startDate);
    if (!plan || !licencesValid || !start) return null;
    return describeChange(mode === 'existing' ? company : null, plan, licenceCount, {
      startDate: start, expiryDate: expiryEdit ? fromInputDate(expiryEdit) : null, plans,
    });
  }, [plan, plans, licencesValid, licenceCount, startDate, expiryEdit, mode, company]);
  const expiryDate = expiryEdit ?? (change ? toInputDate(change.suggestedExpiry) : '');
  const startDay = fromInputDate(startDate);
  const expiryDay = fromInputDate(expiryDate);
  const durationDays = startDay && expiryDay ? daysBetween(startDay, expiryDay) : NaN;

  const clearError = (field) => setErrors((current) => (current[field] ? { ...current, [field]: undefined } : current));

  const chooseCompany = (item) => {
    setCompany(item); setOwnerId(String(item.admins[0].id)); setResults(null); setSearch(''); clearError('company');
  };

  const changeMode = (next) => {
    setMode(next); setCompany(null); setResults(null); setSearch(''); setErrors({}); setSubmitError('');
  };

  const choosePlan = (value) => {
    setPlanId(value); setAmountEdit(null); setExpiryEdit(null); clearError('plan'); clearError('licences');
    const next = plans.find((p) => p.id === Number(value));
    // Keep the licence count if the new plan sells it, otherwise start at the plan's minimum
    const kept = Number(licences);
    setLicences(next ? String(kept >= next.min_licences && kept <= next.max_licences ? kept : next.min_licences) : '');
  };

  const pickFile = (file) => {
    const problem = validateProofFile(file);
    if (problem) { setProof(null); setErrors((current) => ({ ...current, proof: problem })); return; }
    setProof(file); clearError('proof');
  };

  const validate = () => {
    const found = {};
    if (mode === 'existing') {
      if (!company) found.company = 'Search and select the company';
    } else {
      if (newCompany.name.trim().length < 2) found.name = 'Enter the company name';
      if (newCompany.admin_name.trim().length < 2) found.admin_name = 'Enter the administrator’s name';
      if (!EMAIL.test(newCompany.email.trim())) found.email = 'Enter a valid email address';
      if (newCompany.phone.replace(/\D/g, '').length < 10) found.phone = 'Enter a valid phone number';
    }
    if (!plan) found.plan = 'Select a plan';
    if (plan && licenceRangeError) found.licences = licenceRangeError;
    else if (plan && licences === '') found.licences = 'Select the number of licences';
    if (!startDate) found.start = 'Select the start date';
    else if (startDate > todayISO()) found.start = 'The start date cannot be in the future';
    else if (startDate < toInputDate(addDays(new Date(), -MAX_BACKDATE_DAYS))) found.start = `The start date can be at most ${MAX_BACKDATE_DAYS} days ago`;
    if (!expiryDate) found.expiry = 'Select the expiry date';
    else if (startDay && expiryDay <= startDay) found.expiry = 'The expiry date must be after the start date';
    else if (expiryDate <= todayISO()) found.expiry = 'The expiry date has already passed';
    if (!Number.isFinite(amount) || amount <= 0) found.amount = 'Enter the amount received';
    else if (overridden && overrideReason.trim().length < 5) found.override = 'Give the reason for charging a different amount';
    if (reference.trim().length < 4) found.reference = 'Enter the transaction / UTR / cheque reference';
    if (!paymentDate) found.paymentDate = 'Select the payment date';
    else if (paymentDate > todayISO()) found.paymentDate = 'The payment date cannot be in the future';
    const proofProblem = validateProofFile(proof);
    if (proofProblem) found.proof = proofProblem;
    if (!confirmed) found.confirmed = 'Confirm that the payment was received';
    return found;
  };

  const submit = async (event) => {
    event.preventDefault();
    if (submitting) return;
    const found = validate();
    setErrors(found); setSubmitError('');
    if (Object.keys(found).length) {
      setSubmitError('Please fix the highlighted fields.');
      return;
    }
    setSubmitting(true);
    try {
      const result = await manualRegistrationsApi.createRegistration({
        idempotency_key: idempotencyKey.current,
        company_id: mode === 'existing' ? company.id : null,
        owner_user_id: mode === 'existing' ? Number(ownerId) : null,
        new_company: mode === 'new' ? newCompany : null,
        plan_id: plan.id,
        licences: licenceCount,
        start_date: startDate,
        expiry_date: expiryDate,
        duration_days: durationDays,
        amount,
        price_override_reason: overridden ? overrideReason.trim() : '',
        currency: 'INR',
        payment_method: method,
        payment_reference: reference,
        payment_date: paymentDate,
        remarks: remarks.trim(),
        send_email: sendEmail,
        proof,
      });
      toast.success(`${result.registration_no} saved and subscription activated`);
      navigate(`/product-admin/manual-registrations/${result.id}`, { replace: true });
    } catch (err) {
      const message = err.response?.data?.error || 'Could not save the registration. Nothing was changed.';
      setSubmitError(message);
      if (err.response?.status === 409) setErrors((current) => ({ ...current, reference: /reference/i.test(message) ? message : undefined }));
    } finally { setSubmitting(false); }
  };

  const invalid = (field) => (errors[field] ? m.invalid : '');
  const owner = company?.admins.find((a) => String(a.id) === ownerId);

  return (
    <div className={ui.container}>
      <header className={m.headerRow}>
        <div className={ui.pageHeader}>
          <h1 className={ui.title}>New Registration</h1>
          <p className={ui.subtitle}>Record an offline payment and activate the subscription in one step.</p>
        </div>
        <div className={m.headerActions}>
          <Button variant="secondary" onClick={() => navigate('/product-admin/manual-registrations')}>Back to list</Button>
        </div>
      </header>

      <form className={m.form} onSubmit={submit} noValidate>
        {/* 1. Company */}
        <section className={ui.panel}>
          <SectionHeading tone="blue" icon={<BuildingIcon />} title="Company"
            text="Register a new company, or select an existing one (trial, expired or active paid)." />
          <div className={m.segmented} role="tablist" aria-label="Company type">
            <button type="button" role="tab" aria-selected={mode === 'existing'}
              className={`${m.segment} ${mode === 'existing' ? m.segmentActive : ''}`} onClick={() => changeMode('existing')}>
              Existing company
            </button>
            <button type="button" role="tab" aria-selected={mode === 'new'}
              className={`${m.segment} ${mode === 'new' ? m.segmentActive : ''}`} onClick={() => changeMode('new')}>
              New company
            </button>
          </div>
          <div className={m.spacer} />

          {mode === 'existing' && !company && (
            <div className={m.field}>
              <label className={m.label} htmlFor="company-search">Search company<span className={m.required}>*</span></label>
              <span className={`${ui.searchBox} ${m.searchBoxFull}`}>
                <SearchIcon size={16} />
                <input id="company-search" className={`${ui.searchInput} ${invalid('company')}`} value={search} autoComplete="off"
                  onChange={(e) => setSearch(e.target.value)} placeholder="Company name, admin name or email…" />
              </span>
              <span className={m.help}>Type at least 2 characters.</span>
              <FieldError message={errors.company} />
              {searching && <div className={m.emptyResults}>Searching…</div>}
              {!searching && results && (results.length ? (
                <ul className={m.results}>
                  {results.map((item) => (
                    <li key={item.id}>
                      <button type="button" className={m.resultItem} onClick={() => chooseCompany(item)}>
                        <span className={m.resultMain}>
                          <strong>{item.name}</strong>
                          <span>{item.admins.map((a) => a.email).join(', ')}</span>
                        </span>
                        <SubscriptionStateBadge state={item.subscription.state} label={item.subscription.state_label} />
                      </button>
                    </li>
                  ))}
                </ul>
              ) : <div className={m.emptyResults}>No company found. Switch to “New company” to register it.</div>)}
            </div>
          )}

          {mode === 'existing' && company && (
            <div className={m.companyCard}>
              <div className={m.companyCardHead}>
                <div>
                  <strong>{company.name}</strong>
                  <div className={m.help}>{company.admins.length} admin{company.admins.length === 1 ? '' : 's'}</div>
                </div>
                <Button size="sm" variant="secondary" onClick={() => setCompany(null)}>Change company</Button>
              </div>
              <dl className={m.facts}>
                <div className={m.fact}><dt>Current subscription</dt>
                  <dd><SubscriptionStateBadge state={company.subscription.state} label={company.subscription.state_label} /></dd></div>
                <div className={m.fact}><dt>Plan</dt><dd>{company.subscription.plan_name || '—'}</dd></div>
                <div className={m.fact}><dt>Expiry</dt><dd>{company.subscription.expiry ? formatDate(company.subscription.expiry) : '—'}</dd></div>
                <div className={m.fact}><dt>Licences</dt><dd>{formatNumber(company.subscription.licences)}</dd></div>
                <div className={m.fact}><dt>Documents used</dt>
                  <dd>{formatNumber(company.subscription.documents_used)}{company.subscription.document_limit ? ` / ${formatNumber(company.subscription.document_limit)}` : ''}</dd></div>
                <div className={m.fact}><dt>Licence tokens</dt><dd>{formatNumber(company.tokens)}</dd></div>
              </dl>
              {company.admins.length > 1 && (
                <div className={m.field}>
                  <label className={m.label} htmlFor="owner">Subscription owner (administrator)</label>
                  <select id="owner" className={`${ui.control} ${m.input}`} value={ownerId} onChange={(e) => setOwnerId(e.target.value)}>
                    {company.admins.map((a) => <option key={a.id} value={a.id}>{a.name} — {a.email}</option>)}
                  </select>
                  <span className={m.help}>The new subscription is attached to this account; licence tokens keep their own user.</span>
                </div>
              )}
              {company.admins.length === 1 && owner && <div className={m.help}>Subscription owner: {owner.name} ({owner.email})</div>}
            </div>
          )}

          {mode === 'existing' && company && (
            <>
              <div className={m.spacer} />
              <h3 className={m.subheading}>Payment &amp; subscription history</h3>
              <CompanyPaymentHistory items={history} loading={!history && !historyError} error={historyError} />
            </>
          )}

          {mode === 'new' && (
            <div className={m.grid2}>
              <div className={m.field}>
                <label className={m.label} htmlFor="c-name">Company name<span className={m.required}>*</span></label>
                <input id="c-name" className={`${ui.control} ${m.input} ${invalid('name')}`} value={newCompany.name}
                  onChange={(e) => { setNewCompany({ ...newCompany, name: e.target.value }); clearError('name'); }} />
                <FieldError message={errors.name} />
              </div>
              <div className={m.field}>
                <label className={m.label} htmlFor="c-admin">Administrator name<span className={m.required}>*</span></label>
                <input id="c-admin" className={`${ui.control} ${m.input} ${invalid('admin_name')}`} value={newCompany.admin_name}
                  onChange={(e) => { setNewCompany({ ...newCompany, admin_name: e.target.value }); clearError('admin_name'); }} />
                <FieldError message={errors.admin_name} />
              </div>
              <div className={m.field}>
                <label className={m.label} htmlFor="c-email">Administrator email<span className={m.required}>*</span></label>
                <input id="c-email" type="email" className={`${ui.control} ${m.input} ${invalid('email')}`} value={newCompany.email}
                  onChange={(e) => { setNewCompany({ ...newCompany, email: e.target.value }); clearError('email'); }} />
                <FieldError message={errors.email} />
              </div>
              <div className={m.field}>
                <label className={m.label} htmlFor="c-phone">Phone number<span className={m.required}>*</span></label>
                <input id="c-phone" type="tel" className={`${ui.control} ${m.input} ${invalid('phone')}`} value={newCompany.phone}
                  onChange={(e) => { setNewCompany({ ...newCompany, phone: e.target.value }); clearError('phone'); }} />
                <FieldError message={errors.phone} />
              </div>
            </div>
          )}
        </section>

        {/* 2. Subscription */}
        <section className={ui.panel}>
          <SectionHeading tone="green" icon={<FileTextIcon />} title="Subscription details"
            text="The plan and licences the customer paid for." />
          <div className={m.grid3}>
            <div className={m.field}>
              <label className={m.label} htmlFor="plan">Plan<span className={m.required}>*</span></label>
              <select id="plan" className={`${ui.control} ${m.input} ${invalid('plan')}`} value={planId} onChange={(e) => choosePlan(e.target.value)}>
                <option value="">Select a plan</option>
                {plans.map((p) => (
                  <option key={p.id} value={p.id}>{p.name} ({p.min_licences}–{p.max_licences} licences) — {formatINR(p.price_per_licence)} / licence</option>
                ))}
              </select>
              {plan ? (
                <span className={m.help}>
                  {formatINR(plan.price_per_licence)} per licence + {GST_RATE}% GST · {plan.duration_days} days · {plan.min_licences}–{plan.max_licences} licences
                </span>
              ) : <span className={m.help}>Each plan sells its own range of licences.</span>}
              <FieldError message={errors.plan} />
            </div>
            <div className={m.field}>
              <label className={m.label} htmlFor="licences">Licences purchased<span className={m.required}>*</span></label>
              {licenceChoices ? (
                <select id="licences" className={`${ui.control} ${m.input} ${invalid('licences')}`} value={licences}
                  onChange={(e) => { setLicences(e.target.value); setAmountEdit(null); setExpiryEdit(null); clearError('licences'); }}>
                  {licenceChoices.map((n) => <option key={n} value={n}>{n}</option>)}
                </select>
              ) : (
                <input id="licences" type="number" step={1} disabled={!plan} min={plan ? plan.min_licences : undefined}
                  max={plan ? plan.max_licences : undefined} placeholder={plan ? undefined : 'Select a plan first'}
                  className={`${ui.control} ${m.input} ${invalid('licences')}`} value={licences}
                  onChange={(e) => { setLicences(e.target.value); setAmountEdit(null); setExpiryEdit(null); clearError('licences'); }} />
              )}
              <span className={m.help}>
                {plan ? `${plan.name} sells ${plan.min_licences} to ${plan.max_licences} licences.` : 'Select a plan first; its licence range applies.'}
              </span>
              <FieldError message={errors.licences || licenceRangeError} />
            </div>
            <div className={m.field}>
              <label className={m.label} htmlFor="amount">Amount received (INR, incl. GST)<span className={m.required}>*</span></label>
              <input id="amount" inputMode="decimal" className={`${ui.control} ${m.input} ${invalid('amount')}`} value={amountText}
                onChange={(e) => { setAmountEdit(e.target.value); clearError('amount'); }} placeholder="0" />
              {price && (
                <span className={m.help}>
                  List price: {formatINR(plan.price_per_licence)} × {licenceCount} = {formatINR(price.taxable)}
                  {' '}+ {GST_RATE}% GST {formatINR(price.gst)} = <strong>{formatINR(price.total)}</strong>
                </span>
              )}
              <FieldError message={errors.amount} />
            </div>
            <div className={m.field}>
              <label className={m.label} htmlFor="currency">Currency</label>
              <input id="currency" className={`${ui.control} ${m.input} ${m.readonly}`} value="INR (₹)" readOnly />
              <span className={m.help}>Manual payments are recorded in rupees.</span>
            </div>
            <div className={m.field}>
              <label className={m.label} htmlFor="start">Start date<span className={m.required}>*</span></label>
              <input id="start" type="date" max={todayISO()} min={toInputDate(addDays(new Date(), -MAX_BACKDATE_DAYS))}
                className={`${ui.control} ${m.input} ${invalid('start')}`} value={startDate}
                onChange={(e) => { setStartDate(e.target.value); setExpiryEdit(null); clearError('start'); clearError('expiry'); }} />
              <span className={m.help}>Today by default; up to {MAX_BACKDATE_DAYS} days back if the customer paid earlier.</span>
              <FieldError message={errors.start} />
            </div>
            <div className={m.field}>
              <label className={m.label} htmlFor="expiry">Expiry date<span className={m.required}>*</span></label>
              <input id="expiry" type="date" min={startDate || undefined}
                className={`${ui.control} ${m.input} ${invalid('expiry')}`} value={expiryDate}
                onChange={(e) => { setExpiryEdit(e.target.value); clearError('expiry'); }} />
              <span className={m.help}>
                {Number.isFinite(durationDays) && durationDays > 0 ? `${formatNumber(durationDays)} days from the start date` : 'Set from the plan duration'}
                {expiryEdit && change ? ' · edited' : ''}
                {expiryEdit && change && (
                  <> · <button type="button" className={m.linkButton} onClick={() => setExpiryEdit(null)}>Reset to suggested</button></>
                )}
              </span>
              <FieldError message={errors.expiry} />
            </div>
            {overridden && (
              <div className={`${m.field} ${m.fieldFull}`}>
                <div className={`${m.banner} ${m.bannerWarn}`}>
                  The amount ({formatINR(amount)}) differs from the list price ({formatINR(listPrice)}). Give the reason; it is saved with the registration.
                  {' '}<button type="button" className={m.linkButton} onClick={() => { setAmountEdit(null); clearError('override'); }}>Use the list price</button>
                </div>
                <label className={m.label} htmlFor="override">Reason for the different amount<span className={m.required}>*</span></label>
                <input id="override" className={`${ui.control} ${m.input} ${invalid('override')}`} value={overrideReason}
                  onChange={(e) => { setOverrideReason(e.target.value); clearError('override'); }} placeholder="e.g. Discount agreed with the customer" />
                <FieldError message={errors.override} />
              </div>
            )}
          </div>

          {change && (
            <>
              <div className={m.spacer} />
              <div className={m.summaryBox}>
                <strong>What happens when you save · {changeTypeLabel(change.changeType)}</strong>
                <ul>{change.lines.map((line) => <li key={line}>{line}</li>)}</ul>
              </div>
            </>
          )}
        </section>

        {/* 3. Payment */}
        <section className={ui.panel}>
          <SectionHeading tone="amber" icon={<ReceiptIcon />} title="Payment information"
            text="Details of the offline payment, with proof." />
          <div className={m.grid3}>
            <div className={m.field}>
              <label className={m.label} htmlFor="method">Payment method<span className={m.required}>*</span></label>
              <select id="method" className={`${ui.control} ${m.input}`} value={method} onChange={(e) => setMethod(e.target.value)}>
                {PAYMENT_METHODS.map((p) => <option key={p.value} value={p.value}>{p.label}</option>)}
              </select>
            </div>
            <div className={m.field}>
              <label className={m.label} htmlFor="reference">Transaction / UTR reference<span className={m.required}>*</span></label>
              <input id="reference" className={`${ui.control} ${m.input} ${invalid('reference')}`} value={reference} autoComplete="off"
                onChange={(e) => { setReference(e.target.value); clearError('reference'); }} />
              <span className={m.help}>Each reference can be recorded once.</span>
              <FieldError message={errors.reference} />
            </div>
            <div className={m.field}>
              <label className={m.label} htmlFor="pdate">Payment date<span className={m.required}>*</span></label>
              <input id="pdate" type="date" max={todayISO()} className={`${ui.control} ${m.input} ${invalid('paymentDate')}`} value={paymentDate}
                onChange={(e) => { setPaymentDate(e.target.value); clearError('paymentDate'); }} />
              <FieldError message={errors.paymentDate} />
            </div>
            <div className={`${m.field} ${m.fieldFull}`}>
              <label className={m.label} htmlFor="remarks">Remarks (internal)</label>
              <textarea id="remarks" className={m.textarea} value={remarks} maxLength={500}
                onChange={(e) => setRemarks(e.target.value)} placeholder="Anything worth remembering about this payment" />
            </div>

            <div className={`${m.field} ${m.fieldFull}`}>
              <span className={m.label}>Payment screenshot or receipt<span className={m.required}>*</span></span>
              <input ref={fileInput} type="file" accept={PROOF_ACCEPT} className={m.hiddenInput} tabIndex={-1}
                onChange={(e) => { pickFile(e.target.files[0]); e.target.value = ''; }} />
              {!proof ? (
                <div
                  className={`${m.dropzone} ${dragging ? m.dropzoneActive : ''} ${errors.proof ? m.dropzoneInvalid : ''}`}
                  role="button" tabIndex={0} aria-label="Upload payment proof"
                  onClick={() => fileInput.current?.click()}
                  onKeyDown={(e) => { if (e.key === 'Enter' || e.key === ' ') { e.preventDefault(); fileInput.current?.click(); } }}
                  onDragOver={(e) => { e.preventDefault(); setDragging(true); }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => { e.preventDefault(); setDragging(false); pickFile(e.dataTransfer.files[0]); }}
                >
                  <UploadIcon size={26} />
                  <span><strong>Click to upload</strong> or drag and drop</span>
                  <span className={m.help}>PNG, JPG, JPEG or PDF, up to 5 MB</span>
                </div>
              ) : (
                <div className={m.fileChip}>
                  <span className={m.fileThumb}>
                    {proofPreview ? <img src={proofPreview} alt="" /> : 'PDF'}
                  </span>
                  <span className={m.fileInfo}>
                    <strong title={proof.name}>{proof.name}</strong>
                    <span>{formatFileSize(proof.size)} · stored privately after you save</span>
                  </span>
                  <Button size="sm" variant="secondary" onClick={() => fileInput.current?.click()}>Replace</Button>
                  <Button size="sm" variant="secondary" onClick={() => setProof(null)}>Remove</Button>
                </div>
              )}
              <FieldError message={errors.proof} />
            </div>
          </div>
        </section>

        {/* 4. Notifications and confirmation */}
        <section className={ui.panel}>
          <SectionHeading tone="purple" icon={<FileCheckIcon />} title="Confirm and activate"
            text="The subscription is active as soon as you save. No second approval is needed." />
          <div className={m.field}>
            <label className={m.check}>
              <input type="checkbox" checked={sendEmail} onChange={(e) => setSendEmail(e.target.checked)} />
              <span>Send the confirmation and invoice email to the customer. Turn this off to activate without emailing.</span>
            </label>
            <label className={m.check}>
              <input type="checkbox" checked={confirmed} onChange={(e) => { setConfirmed(e.target.checked); clearError('confirmed'); }} />
              <span>
                I confirm that {Number.isFinite(amount) && amount > 0 ? formatINR(amount) : 'the amount'} was received in the bank account
                {reference.trim() ? <> against reference <strong>{reference.trim()}</strong></> : ''}. A screenshot alone does not prove receipt.
              </span>
            </label>
            <FieldError message={errors.confirmed} />
          </div>

          {submitError && <div className={`${m.banner} ${m.bannerError} ${m.bannerTop}`} role="alert">{submitError}</div>}

          <div className={m.actions}>
            <Button variant="secondary" onClick={() => navigate('/product-admin/manual-registrations')} disabled={submitting}>Cancel</Button>
            <Button type="submit" loading={submitting}>Save &amp; Activate</Button>
          </div>
        </section>
      </form>
    </div>
  );
}
